import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './components/ThemeProvider';
import CustomCSSLoader from './components/CustomCSSLoader';
import SiteMetaLoader from './components/SiteMetaLoader';
import CacheVersionChecker from './components/CacheVersionChecker';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import CookiesPolicy from './pages/CookiesPolicy';
import PendingApproval from './pages/PendingApproval';
import ClientQualification from './pages/ClientQualification';
import UserLayout from './components/UserLayout';
import AdminLayout from './components/AdminLayout';
import UserDashboard from './pages/user/Dashboard';
import Deposit from './pages/user/Deposit';
import DepositSuccess from './pages/user/DepositSuccess';
import PaymentSuccess from './pages/user/PaymentSuccess';
import DepositCancel from './pages/user/DepositCancel';
import Withdraw from './pages/user/Withdraw';
import Withdrawals from './pages/user/Withdrawals';
import Deposits from './pages/user/Deposits';
import Transactions from './pages/user/Transactions';
import Rendimentos from './pages/user/Rendimentos';
import Profile from './pages/user/Profile';
import UserStatement from './pages/user/UserStatement';
import FundGrowth from './pages/user/FundGrowth';
import Campaigns from './pages/user/Campaigns';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminDeposits from './pages/admin/Deposits';
import AdminWithdrawals from './pages/admin/Withdrawals';
import WalletManagement from './pages/admin/WalletManagement';
import EditUser from './pages/admin/EditUser';
import UserDetail from './pages/admin/UserDetail';
import ImportExport from './pages/admin/ImportExport';
import CSSEditor from './pages/admin/CSSEditor';
import PendingApprovals from './pages/admin/PendingApprovals';
import PageBuilder from './pages/admin/PageBuilder';
import VisualPageBuilder from './pages/admin/VisualPageBuilder';
import SiteSettings from './pages/admin/SiteSettings';
import LegalPageEditor from './pages/admin/LegalPageEditor';
import MonthlyStatement from './pages/admin/MonthlyStatement';
import CampaignManager from './pages/admin/CampaignManager';
import FundGrowthManager from './pages/admin/FundGrowthManager';
import './index.css';

// Protected Route for Users
const UserRoute = ({ children }) => {
  const { user, loading, isPending } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#1D262D] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  // Redirect pending users to pending approval page
  if (isPending || user.status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }
  
  if (user.is_admin) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Protected Route for Admin
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#1D262D] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route (redirects authenticated users)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#1D262D] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    if (user.is_admin) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/novas-adicoes" element={<LandingPage />} />
      <Route path="/clients" element={<ClientQualification />} />
      <Route path="/register" element={<ClientQualification />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
      
      {/* User Routes */}
      {/* Stripe callback routes - outside auth protection */}
      <Route path="/deposit/success" element={<DepositSuccess />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/deposit/cancel" element={<DepositCancel />} />
      
      {/* Pending Approval Route */}
      <Route path="/pending-approval" element={<PendingApproval />} />

      {/* User Routes */}
      <Route element={<UserRoute><UserLayout /></UserRoute>}>
        <Route path="/dashboard" element={<UserDashboard />} />
        <Route path="/deposit" element={<Deposit />} />
        <Route path="/withdraw" element={<Withdraw />} />
        <Route path="/deposits" element={<Deposits />} />
        <Route path="/withdrawals" element={<Withdrawals />} />
        <Route path="/rendimentos" element={<Rendimentos />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/statement" element={<UserStatement />} />
        <Route path="/fund-growth" element={<FundGrowth />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      
      {/* Admin Routes */}
      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:userId" element={<UserDetail />} />
        <Route path="/admin/users/:userId/edit" element={<EditUser />} />
        <Route path="/admin/wallet/:userId" element={<WalletManagement />} />
        <Route path="/admin/deposits" element={<AdminDeposits />} />
        <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
        <Route path="/admin/import-export" element={<ImportExport />} />
        <Route path="/admin/statement" element={<MonthlyStatement />} />
        <Route path="/admin/css-editor" element={<CSSEditor />} />
        <Route path="/admin/pending-approvals" element={<PendingApprovals />} />
        <Route path="/admin/page-builder" element={<VisualPageBuilder />} />
        <Route path="/admin/page-builder-classic" element={<PageBuilder />} />
        <Route path="/admin/site-settings" element={<SiteSettings />} />
        <Route path="/admin/legal-editor/:pageType" element={<LegalPageEditor />} />
        <Route path="/admin/campaigns" element={<CampaignManager />} />
        <Route path="/admin/fund-growth" element={<FundGrowthManager />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SiteMetaLoader />
          <CustomCSSLoader />
          <CacheVersionChecker />
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
